import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Divider, Typography} from '@material-ui/core'
import ReportSubtitle from 'components/Text/ReportSubtitle'
import useLanguage from 'features/languageReport/useLanguage'
import useStyles from 'components/molcules/N1SummaryReport/useStyles'
import {simpleSanitizeHTML} from 'helpers/analysisHelper'

interface HRVSummaryAutoNervousSystemProps {
  balanceOpinion: string
  balanceCircularImagePath: string
  tableImagePath: string
  contourImagePath: string
  histogramImagePath: string
  contour: number
  histogram: number
}

export default function HRVSummaryAutoNervousSystem(
  props: HRVSummaryAutoNervousSystemProps,
) {
  const {
    balanceCircularImagePath,
    balanceOpinion,
    tableImagePath,
    contourImagePath,
    histogramImagePath,
    contour,
    histogram,
  } = props

  const classes = useStyles()
  const {i18n} = useTranslation()
  const {textHrvAPI} = useLanguage('hrv')

  const hrvVariabilityItems = useMemo(() => {
    return [
      {
        value: `${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage1.main} \n ${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage1.sub}`,
      },
      {
        value: `${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage2.main} \n ${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage2.sub}`,
      },
      {
        value: `${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage3.main} \n ${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage3.sub}`,
      },
      {
        value: `${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage4.main} \n ${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage4.sub}`,
      },
      {
        value: `${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage5.main} \n ${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage5.sub}`,
      },
      {
        value: `${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage6.main} \n ${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage6.sub}`,
      },
      {
        value: `${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage7.main} \n ${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage7.sub}`,
      },
      {
        value: `${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage8.main} \n ${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.two_dimensional_analysis.analysis_results?.opinions.stage8.sub}`,
      },
    ]
  }, [textHrvAPI])
  const hrvHistogramItems = useMemo(() => {
    return [
      {
        value: `${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.histogram_analysis.analysis_results.opinions.stage1.main} \n ${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.histogram_analysis.analysis_results.opinions.stage1.sub}`,
      },
      {
        value: `${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.histogram_analysis.analysis_results.opinions.stage2.main} \n ${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.histogram_analysis.analysis_results.opinions.stage2.sub}`,
      },
      {
        value: `${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.histogram_analysis.analysis_results.opinions.stage3.main} \n ${textHrvAPI?.autonomic_nervous_system_stress_analysis.section.histogram_analysis.analysis_results.opinions.stage3.sub}`,
      },
    ]
  }, [textHrvAPI])

  return (
    <div className={classes.hrv}>
      <br />
      <ReportSubtitle number='1'>
        {textHrvAPI?.autonomic_nervous_system_stress_analysis.title
          .split('<br>')
          .join(' ')}
      </ReportSubtitle>
      <div className={classes.hrvAutoNervous}>
        <img className='TableImage' src={tableImagePath} alt='' />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div className='AutoNervousTitle'>
            {
              textHrvAPI?.autonomic_nervous_system_stress_analysis.section
                .automatic_nervous_balance.pie_chart.title
            }
          </div>
          <img
            className='CircularChartImage'
            src={balanceCircularImagePath}
            alt=''
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 20,
              marginTop: 10,
            }}
          >
            <div className='Sympathetic'>
              <div>
                <span className='Label'>
                  {
                    textHrvAPI?.autonomic_nervous_system_stress_analysis.section
                      .automatic_nervous_balance.pie_chart.annotation
                      .sympathetic.case[0]
                  }
                </span>
                <span className='Sympathetic' style={{fontWeight: 600}}>
                  {
                    textHrvAPI?.autonomic_nervous_system_stress_analysis.section
                      .automatic_nervous_balance.pie_chart.annotation
                      .sympathetic.case[1]
                  }
                </span>
              </div>
              <div
                style={{marginTop: 8}}
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(
                    textHrvAPI?.autonomic_nervous_system_stress_analysis.section
                      .automatic_nervous_balance.pie_chart.annotation
                      .sympathetic.symptoms ?? '',
                  ),
                }}
              />
            </div>
            <div className='Parasympathetic'>
              <div>
                <span className='Label'>
                  {
                    textHrvAPI?.autonomic_nervous_system_stress_analysis.section
                      .automatic_nervous_balance.pie_chart.annotation
                      .parasympathetic.case[0]
                  }
                </span>
                <span className='Parasympathetic' style={{fontWeight: 600}}>
                  {
                    textHrvAPI?.autonomic_nervous_system_stress_analysis.section
                      .automatic_nervous_balance.pie_chart.annotation
                      .parasympathetic.case[1]
                  }
                </span>
              </div>
              <div
                style={{marginTop: 8}}
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(
                    textHrvAPI?.autonomic_nervous_system_stress_analysis.section
                      .automatic_nervous_balance.pie_chart.annotation
                      .parasympathetic.symptoms ?? '',
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.hrvOpinion}>
        <div className='Opinion'>
          <span className='Label'>
            {
              textHrvAPI?.autonomic_nervous_system_stress_analysis.section
                .automatic_nervous_balance.analysis_results.title
            }
          </span>
          <Divider className='Divider' />
        </div>
        <div className='Balance'>{balanceOpinion}</div>
      </div>
      <Divider className={classes.Divider} />
      <Typography className={classes.padding} />
      <Typography className={classes.padding} />
      <Typography className={classes.padding} />
      <ReportSubtitle number='2'>
        {
          textHrvAPI?.autonomic_nervous_system_stress_analysis.section
            .two_dimensional_analysis.title
        }
      </ReportSubtitle>
      <div className={classes.description}>
        {
          textHrvAPI?.autonomic_nervous_system_stress_analysis.section
            .two_dimensional_analysis.description
        }
      </div>
      <div className={classes.hrvAriability}>
        <img className='ContourImage' src={contourImagePath} alt='' />
        <div id='hrv-item'>
          <div className={classes.hrvOpinion}>
            <div className='Opinion'>
              <span className='Label'>
                {
                  textHrvAPI?.autonomic_nervous_system_stress_analysis.section
                    .two_dimensional_analysis.analysis_results.title
                }
              </span>
              <Divider className='Divider' />
            </div>
            <div className='Balance'>
              {i18n.language !== 'ko' &&
                i18n.language !== 'ja' &&
                `${hrvVariabilityItems[contour - 1]?.value}`}
            </div>
            <Divider className={classes.Divider2} />
          </div>
        </div>
      </div>
      <Typography className={classes.padding} />
      <Typography className={classes.padding} />
      <Typography className={classes.padding} />
      <ReportSubtitle number='3'>
        {
          textHrvAPI?.autonomic_nervous_system_stress_analysis.section
            .histogram_analysis.title
        }
      </ReportSubtitle>

      <div className={classes.description}>
        {
          textHrvAPI?.autonomic_nervous_system_stress_analysis.section
            .histogram_analysis.description
        }
      </div>
      <div className={classes.hrvAriability}>
        <img className='ContourImage' src={histogramImagePath} alt='' />
        <div id='hrv-item'>
          <div className={classes.hrvOpinion}>
            <div className='Opinion'>
              <span className='Label'>
                {
                  textHrvAPI?.autonomic_nervous_system_stress_analysis.section
                    .histogram_analysis.analysis_results.title
                }
              </span>
              <Divider className='Divider' />
            </div>
            <div className='Balance'>
              {hrvHistogramItems[histogram - 1]?.value}
            </div>
            <Divider className={classes.Divider2} />
          </div>
        </div>
      </div>
    </div>
  )
}
